import { createFileRoute, Link, useNavigate, useParams } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { adminGetProduct, saveProduct, signProductImage } from "@/lib/products.functions";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { ArrowLeft, Loader2, Save, Trash2, Upload } from "lucide-react";
import { CATEGORIES, CATEGORY_NAMES, SIDES, slugify } from "@/lib/shop";
import { ProductImage } from "@/components/shop/ProductImage";

export const Route = createFileRoute("/_authenticated/admin/products/$id")({
  component: ProductEditor,
});

type Form = {
  id: string | null;
  slug: string; name: string; sku: string; brand: string; category: string; subcategory: string;
  short_description: string; description: string;
  images: string[];
  standard_price: string; physio_price: string; sale_price: string; stock_quantity: string;
  features: string; specifications: string; suitable_for: string; tags: string; body_parts: string;
  conditions: string; variants: string; related_slugs: string; bought_together_slugs: string;
  sides: string[]; sizes: string; colours: string;
  delivery_info: string;
  recommendation_why: string; recommendation_conditions: string; recommendation_advice: string;
  recommendation_precautions: string; recommendation_tips: string;
  featured: boolean; physio_recommended: boolean; physio_purchase_enabled: boolean;
  on_sale: boolean; active: boolean; sort_order: string;
  seo_title: string; seo_description: string;
};

const blank: Form = {
  id: null, slug: "", name: "", sku: "", brand: "", category: "", subcategory: "",
  short_description: "", description: "", images: [],
  standard_price: "", physio_price: "", sale_price: "", stock_quantity: "0",
  features: "", specifications: "", suitable_for: "", tags: "", body_parts: "",
  conditions: "", variants: "", related_slugs: "", bought_together_slugs: "",
  sides: [], sizes: "", colours: "", delivery_info: "",
  recommendation_why: "", recommendation_conditions: "", recommendation_advice: "",
  recommendation_precautions: "", recommendation_tips: "",
  featured: false, physio_recommended: false, physio_purchase_enabled: true,
  on_sale: false, active: false, sort_order: "0", seo_title: "", seo_description: "",
};

const lines = (s: string) => s.split("\n").map((l) => l.trim()).filter(Boolean);
const csv = (s: string) => s.split(",").map((l) => l.trim()).filter(Boolean);
const toText = (a?: string[] | null) => (a ?? []).join("\n");

function fromRow(row: any): Form {
  return {
    ...blank,
    id: row.id,
    slug: row.slug ?? "", name: row.name ?? "", sku: row.sku ?? "", brand: row.brand ?? "",
    category: row.category ?? "", subcategory: row.subcategory ?? "",
    short_description: row.short_description ?? "", description: row.description ?? "",
    images: row.images ?? [],
    standard_price: String(row.standard_price ?? ""),
    physio_price: row.physio_price == null ? "" : String(row.physio_price),
    sale_price: row.sale_price == null ? "" : String(row.sale_price),
    stock_quantity: String(row.stock_quantity ?? 0),
    features: toText(row.features),
    specifications: (row.specifications ?? []).map((s: any) => `${s.label} | ${s.value}`).join("\n"),
    suitable_for: toText(row.suitable_for), tags: toText(row.tags), body_parts: toText(row.body_parts),
    conditions: toText(row.conditions),
    variants: (row.variants ?? []).map((v: any) => `${v.name} | ${(v.options ?? []).join(", ")}`).join("\n"),
    related_slugs: toText(row.related_slugs), bought_together_slugs: toText(row.bought_together_slugs),
    sides: row.sides ?? [], sizes: toText(row.sizes), colours: toText(row.colours),
    delivery_info: row.delivery_info ?? "",
    recommendation_why: row.recommendation_why ?? "",
    recommendation_conditions: row.recommendation_conditions ?? "",
    recommendation_advice: row.recommendation_advice ?? "",
    recommendation_precautions: row.recommendation_precautions ?? "",
    recommendation_tips: row.recommendation_tips ?? "",
    featured: !!row.featured, physio_recommended: !!row.physio_recommended,
    physio_purchase_enabled: row.physio_purchase_enabled ?? true,
    on_sale: !!row.on_sale, active: !!row.active,
    sort_order: String(row.sort_order ?? 0),
    seo_title: row.seo_title ?? "", seo_description: row.seo_description ?? "",
  };
}

function num(v: string) {
  const n = Number(String(v).replace(/[^0-9.]/g, ""));
  return Number.isFinite(n) ? n : 0;
}

function ProductEditor() {
  const { id } = useParams({ from: "/_authenticated/admin/products/$id" });
  const isNew = id === "new";
  const navigate = useNavigate();
  const qc = useQueryClient();
  const get = useServerFn(adminGetProduct);
  const save = useServerFn(saveProduct);
  const sign = useServerFn(signProductImage);

  const [form, setForm] = useState<Form>(blank);
  const [uploading, setUploading] = useState(false);

  const { data: row, isPending } = useQuery({
    queryKey: ["admin", "product", id],
    queryFn: () => get({ data: { id } }),
    enabled: !isNew,
  });

  useEffect(() => {
    if (row) setForm(fromRow(row));
  }, [row]);

  const set = <K extends keyof Form>(k: K, v: Form[K]) => setForm((f) => ({ ...f, [k]: v }));

  const saveMut = useMutation({
    mutationFn: () =>
      save({
        data: {
          id: form.id,
          slug: form.slug.trim() || slugify(form.name),
          name: form.name.trim(),
          sku: form.sku.trim() || null,
          brand: form.brand.trim() || null,
          category: form.category || null,
          subcategory: form.subcategory || null,
          short_description: form.short_description.trim() || null,
          description: form.description.trim() || null,
          images: form.images,
          standard_price: num(form.standard_price),
          physio_price: form.physio_price.trim() ? num(form.physio_price) : null,
          sale_price: form.sale_price.trim() ? num(form.sale_price) : null,
          stock_quantity: Math.round(num(form.stock_quantity)),
          features: lines(form.features),
          specifications: lines(form.specifications).map((l) => {
            const [label, ...rest] = l.split("|");
            return { label: label.trim(), value: rest.join("|").trim() };
          }),
          suitable_for: lines(form.suitable_for),
          tags: lines(form.tags),
          body_parts: lines(form.body_parts),
          conditions: lines(form.conditions),
          variants: lines(form.variants).map((l) => {
            const [name, ...rest] = l.split("|");
            return { name: name.trim(), options: csv(rest.join("|")) };
          }),
          related_slugs: lines(form.related_slugs),
          bought_together_slugs: lines(form.bought_together_slugs),
          sides: form.sides,
          sizes: lines(form.sizes),
          colours: lines(form.colours),
          delivery_info: form.delivery_info.trim() || null,
          recommendation_why: form.recommendation_why.trim() || null,
          recommendation_conditions: form.recommendation_conditions.trim() || null,
          recommendation_advice: form.recommendation_advice.trim() || null,
          recommendation_precautions: form.recommendation_precautions.trim() || null,
          recommendation_tips: form.recommendation_tips.trim() || null,
          featured: form.featured,
          physio_recommended: form.physio_recommended,
          physio_purchase_enabled: form.physio_purchase_enabled,
          on_sale: form.on_sale,
          active: form.active,
          sort_order: Math.round(num(form.sort_order)),
          seo_title: form.seo_title.trim() || null,
          seo_description: form.seo_description.trim() || null,
        },
      }),
    onSuccess: (saved: any) => {
      qc.invalidateQueries({ queryKey: ["admin", "products"] });
      qc.invalidateQueries({ queryKey: ["shop", "products"] });
      toast.success("Product saved");
      if (isNew) navigate({ to: "/admin/products/$id", params: { id: saved.id } });
      else setForm(fromRow(saved));
    },
    onError: (e: Error) => toast.error(e.message),
  });

  async function handleUpload(files: FileList | null) {
    if (!files?.length) return;
    setUploading(true);
    try {
      const urls: string[] = [];
      for (const file of Array.from(files)) {
        const ext = file.name.split(".").pop()?.toLowerCase() || "jpg";
        const path = `${crypto.randomUUID()}.${ext}`;
        const { error } = await supabase.storage.from("product-images").upload(path, file, {
          cacheControl: "31536000",
          upsert: false,
        });
        if (error) throw new Error(error.message);
        const { url } = await sign({ data: { path } });
        urls.push(url);
      }
      set("images", [...form.images, ...urls]);
      toast.success(`${urls.length} image${urls.length === 1 ? "" : "s"} uploaded`);
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setUploading(false);
    }
  }

  const canSave = form.name.trim().length > 0 && (form.slug.trim() || form.name.trim()).length > 0;

  if (!isNew && isPending) {
    return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  }

  const subcats = CATEGORIES.find((c) => c.name === form.category)?.children ?? [];

  return (
    <div className="space-y-6 pb-16">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <Button asChild variant="ghost" size="sm" className="-ml-2 mb-1">
            <Link to="/admin/products"><ArrowLeft className="mr-1 h-4 w-4" />Back to products</Link>
          </Button>
          <h1 className="text-2xl font-semibold tracking-tight">{isNew ? "New product" : form.name || "Edit product"}</h1>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm">
            <Switch checked={form.active} onCheckedChange={(v) => set("active", v)} />
            Published
          </label>
          <Button onClick={() => saveMut.mutate()} disabled={!canSave || saveMut.isPending}>
            {saveMut.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
            Save
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader><CardTitle>Basics</CardTitle></CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-2">
          <Field label="Product name">
            <Input
              value={form.name}
              onChange={(e) => {
                const v = e.target.value;
                setForm((f) => ({ ...f, name: v, slug: f.id || f.slug ? f.slug : slugify(v) }));
              }}
            />
          </Field>
          <Field label="URL slug" hint="Used in the product link, lowercase and hyphens only.">
            <Input value={form.slug} onChange={(e) => set("slug", slugify(e.target.value))} />
          </Field>
          <Field label="SKU"><Input value={form.sku} onChange={(e) => set("sku", e.target.value)} /></Field>
          <Field label="Brand"><Input value={form.brand} onChange={(e) => set("brand", e.target.value)} /></Field>
          <Field label="Category">
            <select
              className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
              value={form.category}
              onChange={(e) => setForm((f) => ({ ...f, category: e.target.value, subcategory: "" }))}
            >
              <option value="">None</option>
              {CATEGORIES.map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
            </select>
          </Field>
          <Field label="Subcategory">
            <select
              className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
              value={form.subcategory}
              onChange={(e) => set("subcategory", e.target.value)}
              disabled={subcats.length === 0}
            >
              <option value="">None</option>
              {subcats.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>
          <Field label="Short description" className="md:col-span-2">
            <Input value={form.short_description} onChange={(e) => set("short_description", e.target.value)} />
          </Field>
          <Field label="Full description" className="md:col-span-2">
            <Textarea rows={6} value={form.description} onChange={(e) => set("description", e.target.value)} />
          </Field>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Images</CardTitle>
          <CardDescription>Upload your own product photos. The first image is the main image.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-3">
            {form.images.map((src, i) => (
              <div key={src} className="relative h-28 w-28 overflow-hidden rounded-lg border">
                <ProductImage src={src} alt={`Image ${i + 1}`} className="h-full w-full" />
                <button
                  type="button"
                  aria-label="Remove image"
                  onClick={() => set("images", form.images.filter((s) => s !== src))}
                  className="absolute right-1 top-1 rounded-full bg-background/90 p-1 text-destructive shadow"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
            <label className="flex h-28 w-28 cursor-pointer flex-col items-center justify-center gap-1 rounded-lg border border-dashed text-xs text-muted-foreground hover:bg-accent">
              {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Upload className="h-5 w-5" />}
              {uploading ? "Uploading" : "Upload"}
              <input
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                disabled={uploading}
                onChange={(e) => { handleUpload(e.target.files); e.target.value = ""; }}
              />
            </label>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Pricing and stock</CardTitle></CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-2">
          <Field label="Standard price (R)">
            <Input inputMode="decimal" value={form.standard_price} onChange={(e) => set("standard_price", e.target.value)} />
          </Field>
          <Field label="Physio guided price (R)" hint="Leave blank to hide the physio guided option.">
            <Input inputMode="decimal" value={form.physio_price} onChange={(e) => set("physio_price", e.target.value)} />
          </Field>
          <Field label="Sale price (R)">
            <Input inputMode="decimal" value={form.sale_price} onChange={(e) => set("sale_price", e.target.value)} />
          </Field>
          <Field label="Stock quantity">
            <Input inputMode="numeric" value={form.stock_quantity} onChange={(e) => set("stock_quantity", e.target.value)} />
          </Field>
          <Field label="Sort order" hint="Lower numbers appear first.">
            <Input inputMode="numeric" value={form.sort_order} onChange={(e) => set("sort_order", e.target.value)} />
          </Field>
          <div className="grid gap-3 self-end">
            <Toggle label="On sale" checked={form.on_sale} onChange={(v) => set("on_sale", v)} />
            <Toggle label="Featured" checked={form.featured} onChange={(v) => set("featured", v)} />
            <Toggle label="Physiotherapist recommended" checked={form.physio_recommended} onChange={(v) => set("physio_recommended", v)} />
            <Toggle label="Allow physio guided purchase" checked={form.physio_purchase_enabled} onChange={(v) => set("physio_purchase_enabled", v)} />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Details</CardTitle>
          <CardDescription>One item per line.</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-2">
          <Field label="Features"><Textarea rows={5} value={form.features} onChange={(e) => set("features", e.target.value)} /></Field>
          <Field label="Specifications" hint="Format: Label | Value">
            <Textarea rows={5} value={form.specifications} onChange={(e) => set("specifications", e.target.value)} />
          </Field>
          <Field label="Suitable for"><Textarea rows={4} value={form.suitable_for} onChange={(e) => set("suitable_for", e.target.value)} /></Field>
          <Field label="Variants" hint="Format: Size | Small, Medium, Large">
            <Textarea rows={4} value={form.variants} onChange={(e) => set("variants", e.target.value)} />
          </Field>
          <Field label="Sizes"><Textarea rows={3} value={form.sizes} onChange={(e) => set("sizes", e.target.value)} /></Field>
          <Field label="Colours"><Textarea rows={3} value={form.colours} onChange={(e) => set("colours", e.target.value)} /></Field>
          <Field label="Sides">
            <div className="flex gap-4 pt-1">
              {SIDES.map((s) => (
                <label key={s} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={form.sides.includes(s)}
                    onChange={(e) =>
                      set("sides", e.target.checked ? [...form.sides, s] : form.sides.filter((x) => x !== s))
                    }
                  />
                  {s}
                </label>
              ))}
            </div>
          </Field>
          <Field label="Delivery information">
            <Textarea rows={3} value={form.delivery_info} onChange={(e) => set("delivery_info", e.target.value)} />
          </Field>
          <Field label="Tags"><Textarea rows={3} value={form.tags} onChange={(e) => set("tags", e.target.value)} /></Field>
          <Field label="Body parts"><Textarea rows={3} value={form.body_parts} onChange={(e) => set("body_parts", e.target.value)} /></Field>
          <Field label="Conditions"><Textarea rows={3} value={form.conditions} onChange={(e) => set("conditions", e.target.value)} /></Field>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Recommended by N S Physiotherapy</CardTitle>
          <CardDescription>Shown on the product page. Leave blank to hide.</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-2">
          <Field label="Why we recommend it"><Textarea rows={4} value={form.recommendation_why} onChange={(e) => set("recommendation_why", e.target.value)} /></Field>
          <Field label="Suitable conditions"><Textarea rows={4} value={form.recommendation_conditions} onChange={(e) => set("recommendation_conditions", e.target.value)} /></Field>
          <Field label="Clinical advice"><Textarea rows={4} value={form.recommendation_advice} onChange={(e) => set("recommendation_advice", e.target.value)} /></Field>
          <Field label="Precautions"><Textarea rows={4} value={form.recommendation_precautions} onChange={(e) => set("recommendation_precautions", e.target.value)} /></Field>
          <Field label="Rehabilitation tips" className="md:col-span-2">
            <Textarea rows={4} value={form.recommendation_tips} onChange={(e) => set("recommendation_tips", e.target.value)} />
          </Field>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Cross sell and SEO</CardTitle>
          <CardDescription>Product slugs, one per line.</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-2">
          <Field label="Related products"><Textarea rows={3} value={form.related_slugs} onChange={(e) => set("related_slugs", e.target.value)} /></Field>
          <Field label="Frequently bought together"><Textarea rows={3} value={form.bought_together_slugs} onChange={(e) => set("bought_together_slugs", e.target.value)} /></Field>
          <Field label="SEO title"><Input value={form.seo_title} onChange={(e) => set("seo_title", e.target.value)} /></Field>
          <Field label="SEO description"><Input value={form.seo_description} onChange={(e) => set("seo_description", e.target.value)} /></Field>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={() => saveMut.mutate()} disabled={!canSave || saveMut.isPending}>
          {saveMut.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
          Save product
        </Button>
      </div>
    </div>
  );
}

function Field({
  label, hint, children, className = "",
}: { label: string; hint?: string; children: React.ReactNode; className?: string }) {
  return (
    <div className={`space-y-1.5 ${className}`}>
      <Label className="text-xs uppercase tracking-wider text-muted-foreground">{label}</Label>
      {children}
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

function Toggle({ label, checked, onChange }: { label: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="flex items-center justify-between gap-4 text-sm">
      <span>{label}</span>
      <Switch checked={checked} onCheckedChange={onChange} />
    </label>
  );
}
